import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Chip,
  Tooltip,
  alpha,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  buildAmortisationClipboardText,
  formatDate,
  formatMoney,
} from "../utils/calculator";

const AmortisationResults = ({
  results,
  onImportClick,
  showImportButton = false,
}) => {
  const [copyNotice, setCopyNotice] = useState("");

  if (!results) return null;

  const { monthlyIrr, amortisation } = results;

  const totalPrincipal = amortisation
    .slice(1)
    .reduce((sum, row) => sum + row.principal, 0);
  const totalInterest = amortisation.reduce(
    (sum, row) => sum + row.interest,
    0
  );
  const totalInstalment = amortisation
    .slice(1)
    .reduce((sum, row) => sum + row.instalment, 0);

  const copyAmortisationTable = async () => {
    const clipboardText = buildAmortisationClipboardText(amortisation);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardText);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = clipboardText;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "fixed";
        textArea.style.top = "-999px";
        document.body.append(textArea);
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }
      setCopyNotice("Amortisation table copied to clipboard.");
      setTimeout(() => setCopyNotice(""), 2500);
    } catch {
      setCopyNotice(
        "Unable to copy automatically. Please select the table and copy it manually."
      );
    }
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography
              variant='caption'
              fontWeight={700}
              letterSpacing={1}
              color='text.secondary'
            >
              CALCULATION COMPLETE
            </Typography>
            <Typography variant='h6' fontWeight={700} color='primary.main'>
              Repayment Breakdown
            </Typography>
          </Box>
          <Box textAlign='right'>
            <Typography
              variant='caption'
              color='text.secondary'
              fontWeight={700}
              letterSpacing={0.8}
            >
              MONTHLY IRR
            </Typography>
            <Typography variant='h5' fontWeight={700} color='primary.main'>
              {(monthlyIrr * 100).toFixed(4)}%
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Effective monthly rate
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Interest is rounded to two decimal places in each period, with any
            final rounding difference adjusted in the last interest expense.
          </Typography>
          <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label='Confirmed schedule'
              color='success'
              variant='outlined'
              size='small'
            />
            <Button
              variant='outlined'
              size='small'
              startIcon={<ContentCopyIcon />}
              onClick={copyAmortisationTable}
            >
              {copyNotice ? "Copied!" : "Copy Table"}
            </Button>
            {showImportButton && onImportClick && (
              <Tooltip title='Confirm and apply this repayment schedule to your Hire Purchase schedule'>
                <Button
                  onClick={onImportClick}
                  variant='contained'
                  color='primary'
                  size='small'
                  startIcon={<CheckCircleOutlineIcon />}
                >
                  Use This Schedule
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <TableContainer sx={{ maxHeight: 480 }}>
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>No.</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Due date</TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>
                  Opening balance
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>
                  Principal
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>
                  Interest
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>
                  Instalment
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>
                  Closing balance
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {amortisation.map((row) => (
                <TableRow key={row.period} hover>
                  <TableCell>{row.period}</TableCell>
                  <TableCell>{formatDate(row.dueDate)}</TableCell>
                  <TableCell align='right'>
                    {formatMoney(row.opening)}
                  </TableCell>
                  <TableCell align='right'>
                    {formatMoney(row.principal)}
                  </TableCell>
                  <TableCell align='right'>
                    {formatMoney(row.interest)}
                  </TableCell>
                  <TableCell align='right'>
                    {formatMoney(row.instalment)}
                  </TableCell>
                  <TableCell align='right'>
                    <Typography component='strong' fontWeight={700}>
                      {formatMoney(row.closing)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                  Total payments
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>
                  {formatMoney(totalPrincipal)}
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>
                  {formatMoney(totalInterest)}
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>
                  {formatMoney(totalInstalment)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {showImportButton && onImportClick && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.06),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.success.main, 0.2),
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            Everything looks good. You can apply this repayment to Hire Purchase
            schedule by clicking the <strong>"Use This Schedule"</strong>{" "}
            button.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AmortisationResults;
